/* Stories — Reels-style vertical video player for ORIO Match.
   Fullscreen. Hides the app MobileNav (z-[400]) via z-[1000] overlay.
   - Header: back arrow + small ORIO wordmark + Nazionale/25km switch.
   - Filters chips (tag).
   - Vertical swipe (drag Y) between stories, autoplay muted, tap to unmute.
   - Right-side action rail: ❤️ heart, 🐾 dog, 💬 (soon), ⤴ share.
   - Bottom author strip: avatar (IG-style circle) → tap opens "Chi ha visto" (Pro).
   - Upload FAB opens a sheet to record/pick video (≤10s ≤15MB).
*/
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Eye, Plus, Volume2, VolumeX, X, Send,
  Loader2, Sparkles, MapPin, Play, Music, MessageCircle, Heart, Camera, Image as ImageIcon, Trash2,
} from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hapticTap } from "@/lib/haptics";
import StoryMusicPicker from "@/components/StoryMusicPicker";

const TAGS = [
  { id: null,      label: "Tutte" },
  { id: "walk",    label: "Passeggiata" },
  { id: "park",    label: "Parco" },
  { id: "social",  label: "Social" },
  { id: "course",  label: "Corso" },
];

export default function Stories({ onBack }) {
  const { user } = useAuth();
  const [scope, setScope] = useState("national"); // 'national' | 'nearby'
  const [tag, setTag] = useState(null);
  const [stories, setStories] = useState([]);
  const [quota, setQuota] = useState(null);
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [viewersOpen, setViewersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reacting, setReacting] = useState(null);
  const [reactions, setReactions] = useState({}); // { storyId: {person: true, dog: true} }
  const [progress, setProgress] = useState(0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [flyHeart, setFlyHeart] = useState(0); // increments each double-tap → animates a giant heart
  const lastTapRef = useRef(0);

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const musicAudioRef = useRef(null);

  // Music sync — when the current story has a music track, play it through
  // a dedicated <audio> element muted-video-style (audio is the source, the
  // video's own soundtrack stays muted). Cleans up on story change so we
  // never get overlapping audio.
  useEffect(() => {
    const s = stories[idx];
    const audio = musicAudioRef.current;
    if (!audio) return;
    if (!s?.music?.url) {
      audio.pause();
      audio.removeAttribute("src");
      return;
    }
    audio.src = buildVideoSrc(s.music.url);
    audio.currentTime = Math.max(0, Number(s.music_start_sec || 0));
    audio.volume = Math.min(1, Math.max(0, Number(s.music_volume ?? 0.8)));
    audio.loop = true;
    audio.muted = muted;  // follow the same mute state as the video
    audio.play().catch(() => {});
    return () => { audio.pause(); };
  }, [idx, stories, muted]);

  // Fetch feed on scope/tag change
  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ scope });
      if (tag) params.set("tag", tag);
      const { data } = await api.get(`/stories/feed?${params.toString()}`);
      setStories(data.stories || []);
      setQuota(data.quota || null);
      setIdx(0);
    } catch (e) {
      console.warn("stories feed error", formatErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [scope, tag]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  // Record a view when the current story changes
  useEffect(() => {
    const s = stories[idx];
    if (!s || String(s.id).startsWith("demo-")) return;
    api.post(`/stories/${s.id}/view`).catch(() => {});
  }, [idx, stories]);

  // Autoplay progress bar (10s max)
  useEffect(() => {
    setProgress(0);
    const s = stories[idx];
    if (!s) return;
    const isImage = s.media_type === "image";
    const dur = isImage ? 5 : Math.min(Number(s.duration_s || 10), 10);
    const start = Date.now();
    const tick = setInterval(() => {
      const p = Math.min(1, (Date.now() - start) / (dur * 1000));
      setProgress(p);
      if (p >= 1) {
        clearInterval(tick);
        // advance to next
        setIdx((i) => (i + 1 < stories.length ? i + 1 : i));
      }
    }, 80);
    return () => clearInterval(tick);
  }, [idx, stories]);

  const current = stories[idx];
  const isOwner = current && user && current.user_id === user.id;
  const isPro = !!(user?.is_pro || user?.is_subscribed);

  const react = async (kind) => {
    if (!current || String(current.id).startsWith("demo-")) {
      hapticTap();
      setReactions((r) => ({
        ...r,
        [current?.id]: { ...(r[current?.id] || {}), [kind]: !((r[current?.id] || {})[kind]) },
      }));
      return;
    }
    if (isOwner) return;
    setReacting(kind);
    try {
      const { data } = await api.post(`/stories/${current.id}/react`, { kind });
      hapticTap();
      setReactions((r) => ({
        ...r,
        [current.id]: { ...(r[current.id] || {}), [kind]: data.state === "added" },
      }));
    } catch (e) {
      console.warn("react error", formatErr(e?.response?.data?.detail));
    } finally {
      setReacting(null);
    }
  };

  // Vertical swipe → change story
  const onDragEnd = (_, info) => {
    if (info.offset.y < -60 && idx + 1 < stories.length) setIdx(idx + 1);
    else if (info.offset.y > 60 && idx > 0) setIdx(idx - 1);
  };

  const shareStory = async () => {
    const url = window.location.origin + "/app/match?tab=stories";
    try {
      if (navigator.share) await navigator.share({ title: "ORIO Stories", url });
      else await navigator.clipboard.writeText(url);
      hapticTap();
    } catch { /* ignore */ }
  };

  return createPortal((
    <div
      ref={containerRef}
      data-testid="stories-fullscreen"
      className="fixed inset-0 z-[2000] bg-black text-white select-none touch-none"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      {/* Hidden audio element for music overlay — synced by the effect above. */}
      <audio ref={musicAudioRef} preload="auto" playsInline data-testid="story-music-audio" />
      {/* --- Top header: back + ORIO logo + scope switch --- */}
      <div className="absolute inset-x-0 top-0 z-30 px-3 pt-3 pointer-events-none">
        {/* Progress dashes */}
        <div className="flex items-center gap-1 mb-2 pointer-events-none">
          {stories.map((_, i) => (
            <div key={i} className="flex-1 h-[3px] rounded-full bg-white/25 overflow-hidden">
              <div
                className="h-full bg-white"
                style={{
                  width: i < idx ? "100%" : i === idx ? `${progress * 100}%` : "0%",
                  transition: i === idx ? "width 80ms linear" : "none",
                }}
              />
            </div>
          ))}
          {stories.length === 0 && <div className="flex-1 h-[3px] rounded-full bg-white/20" />}
        </div>

        <div className="flex items-center justify-between pointer-events-auto">
          <button
            onClick={() => { hapticTap(); onBack(); }}
            data-testid="stories-back"
            className="h-10 w-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center"
            aria-label="Chiudi Stories"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2 select-none" aria-label="ORIO">
            <div className="orio-wordmark-grad h-5 w-[54px]" style={{ filter: "brightness(2)" }} />
            <span className="text-[11px] tracking-[0.22em] uppercase text-white/70">Stories</span>
          </div>

          <ScopeSwitch scope={scope} onChange={setScope} />
        </div>

        {/* Tag filters */}
        <div className="mt-2 pointer-events-auto flex gap-2 overflow-x-auto orio-h-scroll no-scrollbar pb-1">
          {TAGS.map((t) => (
            <button
              key={t.id ?? "all"}
              onClick={() => { hapticTap(); setTag(t.id); }}
              data-testid={`stories-filter-${t.id ?? "all"}`}
              className="px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition"
              style={{
                background: tag === t.id ? "#FF7A1A" : "rgba(255,255,255,0.14)",
                color: tag === t.id ? "white" : "rgba(255,255,255,0.85)",
                border: `1px solid ${tag === t.id ? "#FF7A1A" : "rgba(255,255,255,0.15)"}`,
                backdropFilter: "blur(8px)",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* --- Body: fullscreen story --- */}
      <div className="absolute inset-0">
        {loading ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-white/70">
            <Loader2 className="h-6 w-6 animate-spin" />
            <div className="text-[13px]">Carico le storie…</div>
          </div>
        ) : stories.length === 0 ? (
          <EmptyState onUpload={() => setUploaderOpen(true)} scope={scope} />
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.35}
              onDragEnd={onDragEnd}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.28 }}
              className="absolute inset-0 flex items-center justify-center"
              onClick={(e) => {
                const now = Date.now();
                const isDoubleTap = now - lastTapRef.current < 300;
                lastTapRef.current = now;
                if (isDoubleTap) {
                  // Double-tap → send a ❤️ reaction + animate a giant heart.
                  if (!isOwner && current && !String(current.id).startsWith("demo-")) {
                    react("person");
                  }
                  hapticTap();
                  setFlyHeart((n) => n + 1);
                  e.stopPropagation();
                  return;
                }
                // Single tap → toggle mute (existing behavior).
                setMuted((m) => !m);
              }}
              data-testid="story-card"
            >
              {/* Poster/thumb background */}
              {current.thumb_url && (
                <img
                  src={current.thumb_url}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-40"
                  style={{ filter: "blur(24px) saturate(1.1)" }}
                />
              )}
              {current.media_type === "image" ? (
                <img
                  key={current.video_url}
                  src={buildVideoSrc(current.video_url)}
                  alt=""
                  className="relative z-10 max-h-full max-w-full object-contain"
                  style={{ background: "black" }}
                />
              ) : (
                <video
                  ref={videoRef}
                  key={current.video_url}
                  src={buildVideoSrc(current.video_url)}
                  poster={current.thumb_url}
                  autoPlay
                  loop
                  // Force-mute the video's own audio when the story has a
                  // music track so we don't overlap two audio sources.
                  muted={muted || !!current.music?.url}
                  playsInline
                  className="relative z-10 max-h-full max-w-full object-contain"
                  style={{ background: "black" }}
                />
              )}

              {/* Mute indicator — only for video */}
              {current.media_type !== "image" && (
                <div className="absolute top-24 right-4 z-20 h-9 w-9 rounded-full bg-black/45 backdrop-blur flex items-center justify-center pointer-events-none">
                  {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </div>
              )}

              {/* Flying heart on double-tap — Instagram/TikTok signature */}
              <AnimatePresence>
                {flyHeart > 0 && (
                  <motion.div
                    key={flyHeart}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={{ opacity: [0, 1, 1, 0], scale: [0.4, 1.4, 1.1, 1.6], y: [0, -20, -30, -80] }}
                    transition={{ duration: 1.1, times: [0, 0.2, 0.5, 1], ease: "easeOut" }}
                    className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none"
                    data-testid="story-fly-heart"
                  >
                    <Heart
                      className="h-32 w-32"
                      fill="#FF3355"
                      stroke="#FFFFFF"
                      strokeWidth={2}
                      style={{ filter: "drop-shadow(0 10px 30px rgba(255,51,85,0.6))" }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* --- Right-side action rail --- */}
      {stories.length > 0 && (
        <div className="absolute right-3 z-40 flex flex-col items-center gap-4"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 132px)" }}>
          <ActionButton
            testid="story-react-person"
            label={String(current?.reactions_person || 0)}
            active={!!(reactions[current?.id]?.person)}
            emoji="❤️"
            onClick={() => react("person")}
            disabled={isOwner || reacting === "person"}
          />
          <ActionButton
            testid="story-react-dog"
            label={String(current?.reactions_dog || 0)}
            active={!!(reactions[current?.id]?.dog)}
            emoji="🐾"
            onClick={() => react("dog")}
            disabled={isOwner || reacting === "dog"}
          />
          <ActionButton
            testid="story-comments"
            label={String(current?.comments_count || 0)}
            icon={<MessageCircle className="h-5 w-5" />}
            onClick={() => { hapticTap(); setCommentsOpen(true); }}
          />
          <ActionButton
            testid="story-share"
            emoji="↗"
            label="Share"
            onClick={shareStory}
          />
        </div>
      )}

      {/* --- Bottom author strip --- */}
      {current && (
        <div className="absolute inset-x-0 z-30 px-4"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)" }}>
          <div className="flex items-end justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {/* IG-style ring around avatar, tap → Chi ha visto (Pro) */}
              <button
                onClick={() => { if (isOwner) setViewersOpen(true); }}
                data-testid="story-author-avatar"
                className="relative shrink-0"
                aria-label={isOwner ? "Vedi chi ha guardato" : "Autore"}
              >
                <div
                  className="p-[2px] rounded-full"
                  style={{ background: isOwner
                    ? "conic-gradient(from 0deg, #FF7A1A, #F4B83C, #FF7A1A)"
                    : "linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,255,255,0.3))" }}
                >
                  <div className="h-12 w-12 rounded-full bg-black p-[2px]">
                    <img
                      src={(current.author?.photo_url) || "https://ui-avatars.com/api/?name=" + encodeURIComponent(current.author?.name || "?") + "&background=FF7A1A&color=fff"}
                      alt=""
                      className="h-full w-full rounded-full object-cover"
                    />
                  </div>
                </div>
                {isOwner && (
                  <span className="absolute -bottom-1 -right-1 bg-white text-[#FF7A1A] rounded-full h-5 w-5 flex items-center justify-center text-[10px] font-bold">
                    <Eye className="h-3 w-3" />
                  </span>
                )}
              </button>

              <div className="min-w-0">
                <div className="font-semibold text-[15px] truncate">
                  {current.author?.name || "Utente ORIO"}
                </div>
                <div className="text-[11.5px] text-white/70 flex items-center gap-1.5 truncate">
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span className="truncate">{current.location_label || current.author?.city || "ORIO Community"}</span>
                  {current.tag && (
                    <span className="ml-1 px-1.5 py-[1px] rounded-full text-[10px] uppercase tracking-wide font-bold"
                      style={{ background: "rgba(255,255,255,0.15)" }}>
                      {current.tag}
                    </span>
                  )}
                </div>
                {/* Caption — elegant IG-style short overlay */}
                {current.caption && (
                  <div
                    className="mt-1 text-[13px] text-white/95 italic leading-snug"
                    style={{
                      display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                      overflow: "hidden", textShadow: "0 1px 4px rgba(0,0,0,0.55)",
                      maxWidth: "78vw",
                    }}
                    data-testid="story-caption"
                  >
                    {current.caption}
                  </div>
                )}
                {/* Music badge — small marquee-style ticker */}
                {current.music?.title && (
                  <div className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur-sm text-[10.5px] font-semibold"
                       data-testid="story-music-badge">
                    <Music className="h-2.5 w-2.5" />
                    <span className="truncate max-w-[160px]">{current.music.title} · {current.music.artist}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Upload FAB */}
            <button
              onClick={() => { hapticTap(); setUploaderOpen(true); }}
              data-testid="stories-upload-fab"
              className="shrink-0 h-12 px-4 rounded-full flex items-center gap-2 font-bold text-[13px]"
              style={{
                background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                boxShadow: "0 10px 26px -8px rgba(255,122,26,0.65)",
                color: "white",
              }}
            >
              <Plus className="h-4 w-4" /> Storia
            </button>
          </div>
        </div>
      )}

      {/* --- Modals --- */}
      <AnimatePresence>
        {uploaderOpen && (
          <StoryUploader
            quota={quota}
            onClose={() => setUploaderOpen(false)}
            onCreated={() => { setUploaderOpen(false); loadFeed(); }}
          />
        )}
        {viewersOpen && current && (
          <ViewersSheet
            storyId={current.id}
            isPro={isPro}
            onClose={() => setViewersOpen(false)}
          />
        )}
        {commentsOpen && current && (
          <CommentsSheet
            story={current}
            isOwner={isOwner}
            onClose={() => setCommentsOpen(false)}
            onCountChange={(newCount) => {
              setStories((all) => all.map((s) =>
                s.id === current.id ? { ...s, comments_count: newCount } : s
              ));
            }}
          />
        )}
      </AnimatePresence>
    </div>
  ), document.body);
}

/* --- Helpers ------------------------------------------------------------- */

function buildVideoSrc(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  // Backend-served path like /api/static/story_videos/xxx.mp4
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

function ScopeSwitch({ scope, onChange }) {
  return (
    <div
      className="inline-flex rounded-full p-[3px] text-[12px] font-semibold"
      style={{ background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.15)", backdropFilter: "blur(8px)" }}
    >
      {[
        { id: "national", label: "Nazionale" },
        { id: "nearby",   label: "25km" },
      ].map((o) => {
        const active = scope === o.id;
        return (
          <button
            key={o.id}
            data-testid={`stories-scope-${o.id}`}
            onClick={() => { hapticTap(); onChange(o.id); }}
            className="px-3 py-1.5 rounded-full transition"
            style={{
              background: active ? "white" : "transparent",
              color: active ? "#0F0F0F" : "rgba(255,255,255,0.85)",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ActionButton({ emoji, icon, label, onClick, active, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="flex flex-col items-center gap-1"
      style={{ opacity: disabled ? 0.5 : 1 }}
    >
      <div
        className="h-12 w-12 rounded-full flex items-center justify-center text-xl transition text-white"
        style={{
          background: active ? "#FF7A1A" : "rgba(0,0,0,0.4)",
          border: `1px solid ${active ? "#FF7A1A" : "rgba(255,255,255,0.20)"}`,
          backdropFilter: "blur(10px)",
          transform: active ? "scale(1.08)" : "scale(1)",
        }}
      >
        {icon ? icon : <span>{emoji}</span>}
      </div>
      <div className="text-[11px] font-semibold text-white/85">{label}</div>
    </button>
  );
}

function EmptyState({ onUpload, scope }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-4 px-8 text-center">
      <div className="h-20 w-20 rounded-full flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #FF7A1A22, #F4B83C22)", border: "1px solid rgba(255,255,255,0.15)" }}>
        <Sparkles className="h-8 w-8 text-white/80" />
      </div>
      <div>
        <div className="font-display text-2xl">Nessuna storia {scope === "nearby" ? "nei 25km" : "attiva"}</div>
        <div className="text-[13px] text-white/60 mt-1 max-w-sm">
          Sii tu il primo a raccontare com&apos;è oggi la vostra passeggiata. Il video dura 12 ore e poi sparisce.
        </div>
      </div>
      <button
        onClick={onUpload}
        data-testid="stories-empty-upload"
        className="mt-2 px-5 py-3 rounded-full font-bold text-[14px] inline-flex items-center gap-2"
        style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)", color: "white",
          boxShadow: "0 12px 30px -8px rgba(255,122,26,0.6)" }}
      >
        <Plus className="h-4 w-4" /> Pubblica una storia
      </button>
    </div>
  );
}

/* --- Uploader ------------------------------------------------------------ */

export function StoryUploader({ onClose, onCreated, quota }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'video' | 'image'
  const [dims, setDims] = useState(null); // {w, h}
  const [tag, setTag] = useState(null);
  const [locationLabel, setLocationLabel] = useState("");
  const [caption, setCaption] = useState("");
  const [music, setMusic] = useState(null); // {id, title, artist, url, cover_url, duration_s, start_sec}
  const [musicPickerOpen, setMusicPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  // Dog tag — when set, story becomes a permanent Highlight on that dog's
  // public passport once it expires (12h). Auto-select if the user has 1 dog.
  const [myDogs, setMyDogs] = useState([]);
  const [dogId, setDogId] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/dogs");
        if (!alive) return;
        const list = Array.isArray(data) ? data : (data?.dogs || []);
        setMyDogs(list);
        if (list.length === 1) setDogId(list[0].id);
      } catch { /* silent — user might have no dogs yet */ }
    })();
    return () => { alive = false; };
  }, []);

  const VIDEO_MIME = ["video/mp4","video/quicktime","video/webm"];
  const IMAGE_MIME = ["image/jpeg","image/png","image/webp"];

  const measureImage = (url) => new Promise((res) => {
    const img = new Image();
    img.onload = () => res({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => res(null);
    img.src = url;
  });
  const measureVideo = (url) => new Promise((res) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => res({ w: v.videoWidth, h: v.videoHeight });
    v.onerror = () => res(null);
    v.src = url;
  });

  /* Client-side thumbnail generation for video stories.
     Grabs a frame at ~0.5s via a hidden <video> + canvas → JPEG blob.
     Uploaded as /stories/upload-image so the resulting URL can be set as
     `thumb_url` on the created story. Prevents the "black preview" bug on
     the Home carousel where the <video> hasn't decoded a frame yet. */
  const generateVideoThumb = async (videoFile) => {
    const url = URL.createObjectURL(videoFile);
    try {
      const v = document.createElement("video");
      v.preload = "auto";
      v.muted = true;
      v.playsInline = true;
      v.src = url;
      await new Promise((res, rej) => {
        const t = setTimeout(() => rej(new Error("timeout")), 6000);
        v.onloadeddata = () => { clearTimeout(t); res(); };
        v.onerror = () => { clearTimeout(t); rej(new Error("load-error")); };
      });
      // Seek to first ~0.5s (or 10% of duration if shorter) — avoids the
      // usual black initial frame that some codecs produce.
      const target = Math.min(0.5, Math.max(0.1, (v.duration || 1) * 0.1));
      await new Promise((res) => {
        const t = setTimeout(res, 2500);
        v.onseeked = () => { clearTimeout(t); res(); };
        v.currentTime = target;
      });
      const canvas = document.createElement("canvas");
      const w = v.videoWidth, h = v.videoHeight;
      if (!w || !h) throw new Error("no-dims");
      // Cap width at 480px for a light thumbnail
      const scale = Math.min(1, 480 / w);
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
      if (!blob) throw new Error("no-blob");
      // Upload the thumb as an "image" story asset — same folder, same TTL
      const fd = new FormData();
      fd.append("file", new File([blob], "thumb.jpg", { type: "image/jpeg" }));
      const up = await api.post("/stories/upload-image", fd,
        { headers: { "Content-Type": "multipart/form-data" }});
      return up.data.url;
    } catch (e) {
      // Silent — a missing thumb only downgrades the preview (still no black
      // square thanks to the gradient fallback in HomeStoriesStrip).
      return null;
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const pick = async (f) => {
    if (!f) return;
    const isVid = VIDEO_MIME.includes(f.type);
    const isImg = IMAGE_MIME.includes(f.type);
    if (!isVid && !isImg) {
      setErr("Formato non supportato (usa mp4/mov/webm o jpg/png/webp).");
      return;
    }
    const maxBytes = isVid ? 15 * 1024 * 1024 : 8 * 1024 * 1024;
    if (f.size > maxBytes) {
      setErr(`File troppo grande (max ${maxBytes / (1024 * 1024)}MB).`);
      return;
    }
    const url = URL.createObjectURL(f);
    const d = isVid ? await measureVideo(url) : await measureImage(url);
    if (!d || d.w === 0) {
      setErr("Impossibile leggere il file.");
      return;
    }
    if (d.w > d.h * 0.95) {
      setErr("Le storie ORIO sono verticali. Scegli un file in formato ritratto (9:16).");
      URL.revokeObjectURL(url);
      return;
    }
    setErr(null);
    setFile(f);
    setPreview(url);
    setMediaType(isVid ? "video" : "image");
    setDims(d);
  };

  const submit = async () => {
    if (!file) { setErr("Scegli un video o una foto prima di pubblicare."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const endpoint = mediaType === "image"
        ? "/stories/upload-image"
        : "/stories/upload-video";
      const up = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" }});
      // Generate + upload a thumbnail for video stories (best-effort)
      let thumbUrl = null;
      if (mediaType === "video") {
        thumbUrl = await generateVideoThumb(file);
      }
      const payload = {
        video_url: up.data.url,
        thumb_url: thumbUrl || undefined,
        media_type: mediaType,
        duration_s: mediaType === "image" ? 5 : 10,
        tag,
        dog_id: dogId || undefined,
        location_label: locationLabel || undefined,
        caption: caption.trim() || undefined,
        music_track_id: music?.id || undefined,
        music_start_sec: music?.start_sec || 0,
        music_volume: 0.8,
      };
      if (navigator.geolocation) {
        await new Promise((res) => {
          navigator.geolocation.getCurrentPosition(
            (p) => { payload.lat = p.coords.latitude; payload.lng = p.coords.longitude; res(); },
            () => res(),
            { timeout: 1500, maximumAge: 60000 }
          );
        });
      }
      await api.post("/stories", payload);
      onCreated?.();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || "Errore in fase di pubblicazione.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1200] bg-black/70 flex items-end justify-center"
      onClick={onClose}
      data-testid="stories-uploader"
    >
      <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 260 }}
        className="w-full max-w-lg bg-[#FBF7EF] text-[#1a1a1a] rounded-t-[28px] p-5 pb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-xl">Nuova storia</div>
            <div className="text-[12px] text-[#7A7A7A]">Foto o video verticale · sparisce dopo 12h</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-black/5 flex items-center justify-center"
            aria-label="Chiudi">
            <X className="h-4 w-4" />
          </button>
        </div>

        {quota && (
          <div className="mb-3 flex items-center gap-2 text-[11.5px]">
            <span className="px-2 py-1 rounded-full font-semibold"
              style={{ background: quota.remaining > 0 ? "#E8F8EE" : "#FFEEDD",
                       color: quota.remaining > 0 ? "#1E7E3F" : "#C25612" }}>
              {quota.remaining > 0
                ? `${quota.remaining}/${quota.limit} disponibili oggi`
                : `Quota finita — extra ${quota.karma_cost_extra} karma (hai ${quota.karma_have})`}
            </span>
          </div>
        )}

        {/* File picker area */}
        {preview ? (
          <div className="relative rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: "9/16", maxHeight: "50vh" }}>
            {mediaType === "video" ? (
              <video src={preview} className="w-full h-full object-contain" controls playsInline />
            ) : (
              <img src={preview} className="w-full h-full object-contain" alt="Preview" />
            )}
            <button
              onClick={() => { setFile(null); setPreview(null); setMediaType(null); setDims(null); }}
              className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center"
              aria-label="Rimuovi"
            ><X className="h-4 w-4" /></button>
            {dims && (
              <div className="absolute bottom-2 left-2 px-2 py-1 rounded-full text-[10px] font-semibold text-white bg-black/60">
                {mediaType === "image" ? "📷" : "🎬"} {dims.w}×{dims.h}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => cameraInputRef.current?.click()}
              data-testid="stories-uploader-camera"
              className="rounded-2xl p-6 flex flex-col items-center justify-center gap-2 border-2 active:scale-[0.98] transition-transform"
              style={{ borderColor: "#FF7A1A55", background: "linear-gradient(135deg,#FFF6E6 0%, #FFE0BE 100%)" }}
            >
              <div className="h-12 w-12 rounded-full flex items-center justify-center"
                   style={{ background: "linear-gradient(135deg,#FF7A1A,#F4B83C)", boxShadow: "0 8px 20px -6px rgba(255,122,26,0.55)" }}>
                <Camera className="h-5 w-5 text-white" />
              </div>
              <div className="font-bold text-[13.5px]">Scatta ora</div>
              <div className="text-[10.5px] text-[#7A7A7A] text-center leading-tight">Apri fotocamera</div>
            </button>
            <button
              onClick={() => galleryInputRef.current?.click()}
              data-testid="stories-uploader-gallery"
              className="rounded-2xl p-6 flex flex-col items-center justify-center gap-2 border-2 active:scale-[0.98] transition-transform"
              style={{ borderColor: "#F0EBE3", background: "white" }}
            >
              <div className="h-12 w-12 rounded-full flex items-center justify-center"
                   style={{ background: "linear-gradient(135deg,#FF7A1A22,#F4B83C22)" }}>
                <ImageIcon className="h-5 w-5 text-[#C25612]" />
              </div>
              <div className="font-bold text-[13.5px]">Da galleria</div>
              <div className="text-[10.5px] text-[#7A7A7A] text-center leading-tight">Foto o video</div>
            </button>
          </div>
        )}
        <div className="text-[10.5px] text-[#9A9A9A] mt-2 text-center">
          Solo formato verticale (9:16) · MP4/MOV/WebM ≤15MB · JPG/PNG ≤8MB
        </div>
        {/* Hidden inputs — camera opens the OS camera app directly on mobile
            (capture attribute + image-only accept), gallery opens the OS picker.
            Note: `hidden` breaks `capture` on some browsers (iOS Safari, Chrome
            Android PWA); we use absolute off-screen positioning instead. */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp"
          style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }}
        />

        {/* Tag chips */}
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A] mb-2">Contesto</div>
          <div className="flex gap-2 flex-wrap">
            {TAGS.filter((t) => t.id).map((t) => {
              const active = tag === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTag(active ? null : t.id)}
                  data-testid={`stories-uploader-tag-${t.id}`}
                  className="px-3 py-1.5 rounded-full text-[12px] font-semibold border"
                  style={{
                    background: active ? "#FF7A1A" : "white",
                    borderColor: active ? "#FF7A1A" : "#F0EBE3",
                    color: active ? "white" : "#1a1a1a",
                  }}
                >{t.label}</button>
              );
            })}
          </div>
        </div>

        {/* Dog tag — makes the story a permanent HIGHLIGHT on the dog's
            public passport after 12h. Optional. */}
        {myDogs.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A]">
                Tagga il tuo cane
              </div>
              <div className="text-[10px] text-[#7A7A7A]">
                Diventa parte del suo diario
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {myDogs.map((d) => {
                const active = dogId === d.id;
                return (
                  <button
                    key={d.id}
                    onClick={() => { hapticTap(); setDogId(active ? null : d.id); }}
                    data-testid={`stories-uploader-dog-${d.id}`}
                    className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border text-[12.5px] font-semibold"
                    style={{
                      background: active ? "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" : "white",
                      borderColor: active ? "#FF7A1A" : "#F0EBE3",
                      color: active ? "white" : "#1a1a1a",
                      boxShadow: active ? "0 6px 16px -6px rgba(255,122,26,0.55)" : "none",
                    }}
                  >
                    <span className="h-6 w-6 rounded-full overflow-hidden bg-white flex items-center justify-center border border-white/70 shrink-0">
                      {d.photo_url ? (
                        <img src={d.photo_url} alt="" className="h-full w-full object-cover"
                             onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <span className="text-[10px] font-bold text-[#FF7A1A]">
                          {(d.name || "?").charAt(0).toUpperCase()}
                        </span>
                      )}
                    </span>
                    {d.name || "Cane"}
                  </button>
                );
              })}
            </div>
            {dogId && (
              <div className="mt-1.5 text-[10.5px] text-[#7A7A7A] pl-1">
                📖 Alla scadenza (12h) questa storia entrerà nel diario pubblico di {myDogs.find((x) => x.id === dogId)?.name || "questo cane"}.
              </div>
            )}
          </div>
        )}

        {/* Location label */}
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A] mb-2">Luogo (opzionale)</div>
          <input
            value={locationLabel}
            onChange={(e) => setLocationLabel(e.target.value)}
            placeholder="Es. Milano · Parco Sempione"
            className="w-full rounded-xl px-3 py-2.5 text-[14px] bg-white border border-[#F0EBE3] outline-none focus:border-[#FF7A1A]"
            data-testid="stories-uploader-location"
          />
        </div>

        {/* Caption — short IG-style text overlaid under the author name */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A]">Didascalia (opzionale)</div>
            <div className="text-[10px] text-[#9A9A9A] tabular-nums">{caption.length}/140</div>
          </div>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 140))}
            placeholder="Racconta questa storia in poche parole…"
            rows={2}
            className="w-full rounded-xl px-3 py-2.5 text-[14px] bg-white border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] resize-none"
            data-testid="stories-uploader-caption"
          />
        </div>

        {/* Music picker toggle */}
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A] mb-2">Musica (opzionale)</div>
          <button
            onClick={() => setMusicPickerOpen(true)}
            data-testid="stories-uploader-music"
            className="w-full rounded-xl p-3 flex items-center gap-3 bg-white border border-[#F0EBE3] active:scale-[0.98] transition-transform"
          >
            <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                 style={{
                   background: music
                     ? "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)"
                     : "linear-gradient(135deg,#FF7A1A22,#F4B83C22)",
                 }}>
              <Music className={`h-4 w-4 ${music ? "text-white" : "text-[#C25612]"}`} />
            </div>
            <div className="min-w-0 flex-1 text-left">
              {music ? (
                <>
                  <div className="font-semibold text-[13.5px] truncate">{music.title}</div>
                  <div className="text-[11px] text-[#7A7A7A] truncate">{music.artist} · da {Math.floor(music.start_sec || 0)}s</div>
                </>
              ) : (
                <>
                  <div className="font-semibold text-[13.5px]">Aggiungi musica</div>
                  <div className="text-[11px] text-[#7A7A7A]">12 brani royalty-free · IG-style</div>
                </>
              )}
            </div>
            {music && (
              <span
                onClick={(e) => { e.stopPropagation(); setMusic(null); }}
                className="h-7 w-7 rounded-full bg-black/5 flex items-center justify-center"
                aria-label="Rimuovi musica"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
          </button>
        </div>

        {err && <div className="mt-3 text-[12.5px] text-[#C25612] bg-[#FFEEDD] rounded-xl px-3 py-2">{err}</div>}

        <button
          onClick={submit}
          disabled={uploading || !file}
          data-testid="stories-uploader-submit"
          className="mt-5 w-full h-12 rounded-full font-bold text-[14.5px] inline-flex items-center justify-center gap-2"
          style={{
            background: !file ? "#E5DECF" : "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
            color: !file ? "#9A9A9A" : "white",
            boxShadow: !file ? "none" : "0 12px 30px -8px rgba(255,122,26,0.5)",
          }}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {uploading ? "Pubblico…" : "Pubblica storia"}
        </button>
      </motion.div>

      {/* Music picker overlay — sits above the uploader sheet */}
      <AnimatePresence>
        {musicPickerOpen && (
          <StoryMusicPicker
            initialTrackId={music?.id}
            initialStart={music?.start_sec || 0}
            onClose={() => setMusicPickerOpen(false)}
            onSelect={(t) => { setMusic(t); setMusicPickerOpen(false); }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* --- Chi ha visto (Pro) -------------------------------------------------- */

function ViewersSheet({ storyId, isPro, onClose }) {
  const [views, setViews] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [proBlocked, setProBlocked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/stories/${storyId}/viewers`);
        setViews(data.views || []);
        setCount(data.count || 0);
      } catch (e) {
        if (e?.response?.status === 402) setProBlocked(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [storyId]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1200] bg-black/70 flex items-end justify-center"
      onClick={onClose}
      data-testid="stories-viewers-sheet"
    >
      <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 260 }}
        className="w-full max-w-lg bg-[#FBF7EF] text-[#1a1a1a] rounded-t-[28px] p-5 pb-8 max-h-[80vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-xl inline-flex items-center gap-2">
              <Eye className="h-5 w-5 text-[#FF7A1A]" /> Chi ha visto
            </div>
            <div className="text-[12px] text-[#7A7A7A]">{count} visualizzazioni</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-black/5 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading && <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#FF7A1A]"/></div>}
        {proBlocked && !isPro && (
          <div className="p-4 rounded-2xl bg-gradient-to-br from-[#FF7A1A15] to-[#F4B83C15] border border-[#FF7A1A33]">
            <div className="font-semibold">Riservato a ORIO Pro</div>
            <div className="text-[13px] text-[#6B6B6B] mt-1">
              Scopri chi ha guardato le tue storie e trasforma le visualizzazioni in match.
            </div>
          </div>
        )}
        {!loading && !proBlocked && views.length === 0 && (
          <div className="py-8 text-center text-[13px] text-[#7A7A7A]">
            Nessuno ha ancora guardato questa storia.
          </div>
        )}
        <div className="space-y-2 mt-2">
          {views.map((v) => (
            <div key={v.id} className="flex items-center gap-3 p-2 rounded-xl bg-white border border-[#F0EBE3]">
              <img
                src={v.viewer?.photo_url || "https://ui-avatars.com/api/?name=" + encodeURIComponent(v.viewer?.name || "?") + "&background=FF7A1A&color=fff"}
                alt="" className="h-10 w-10 rounded-full object-cover"
              />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[14px] truncate">{v.viewer?.name || "Utente"}</div>
                <div className="text-[11.5px] text-[#7A7A7A] truncate">{v.viewer?.city || "ORIO"}</div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}


/* --- CommentsSheet — Instagram-style bottom sheet of comments (100% free) ---
   Free ORIO feature (no message cap). Loads /api/stories/{id}/comments,
   allows posting a comment (POST). Not part of the PRO messaging quota. */
function CommentsSheet({ story, isOwner, onClose, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/stories/${story.id}/comments`, { params: { limit: 100 } });
        if (!alive) return;
        setComments(Array.isArray(data?.comments) ? data.comments : []);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [story.id]);

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed || posting) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/stories/${story.id}/comments`, { text: trimmed });
      const newComment = data?.comment;
      if (newComment) {
        setComments((prev) => [newComment, ...prev]);
        onCountChange?.((story.comments_count || 0) + comments.length + 1);
      }
      setText("");
      hapticTap();
    } catch (e) {
      console.warn("comment error", formatErr(e?.response?.data?.detail));
    } finally {
      setPosting(false);
      inputRef.current?.focus();
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/stories/${story.id}/comments/${c.id}`);
      setComments((prev) => prev.filter((x) => x.id !== c.id));
      onCountChange?.(Math.max(0, (story.comments_count || 0) - 1));
    } catch { /* silent */ }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[2100] bg-black/60 flex items-end justify-center"
      onClick={onClose}
      data-testid="story-comments-sheet"
    >
      <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 260 }}
        className="w-full max-w-lg bg-[#FBF7EF] text-[#1a1a1a] rounded-t-[28px] flex flex-col"
        style={{ maxHeight: "78vh", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-3 flex justify-center">
          <div className="h-1 w-10 rounded-full bg-black/15" />
        </div>
        <div className="flex items-center justify-between px-5 pt-3 pb-2">
          <div>
            <div className="font-display text-lg inline-flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-[#FF7A1A]" /> Commenti
            </div>
            <div className="text-[11.5px] text-[#7A7A7A]">
              {comments.length === 0 ? "Sii il primo a commentare" : `${comments.length} commenti`}
            </div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-black/5 flex items-center justify-center" aria-label="Chiudi">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-3">
          {loading && (
            <div className="py-8 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-[#FF7A1A]" />
            </div>
          )}
          {!loading && comments.length === 0 && (
            <div className="py-10 text-center">
              <div className="text-3xl mb-2">💬</div>
              <div className="text-[13px] text-[#7A7A7A]">Nessun commento ancora.<br/>Rompi il ghiaccio con un saluto.</div>
            </div>
          )}
          <div className="space-y-3 pt-1">
            {comments.map((c) => (
              <div key={c.id} className="flex items-start gap-2.5" data-testid={`story-comment-${c.id}`}>
                <img
                  src={c.user_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.user_name || "?")}&background=FF7A1A&color=fff`}
                  alt="" className="h-8 w-8 rounded-full object-cover shrink-0 border border-white"
                  onError={(e) => { e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(c.user_name || "?")}&background=FF7A1A&color=fff`; }}
                />
                <div className="min-w-0 flex-1">
                  <div className="rounded-2xl px-3 py-2 bg-white border border-[#F0EBE3]">
                    <div className="text-[12.5px] font-semibold text-[#1A1A1A] leading-tight">{c.user_name || "Utente ORIO"}</div>
                    <div className="text-[13.5px] text-[#1A1A1A] leading-snug mt-0.5 break-words">{c.text}</div>
                  </div>
                  <div className="text-[10.5px] text-[#9A9A9A] mt-0.5 pl-2">
                    {timeSince(c.created_at)}
                  </div>
                </div>
                {(isOwner || null) && (
                  <button
                    onClick={() => remove(c)}
                    className="h-8 w-8 rounded-full flex items-center justify-center text-[#C25612]"
                    aria-label="Elimina commento"
                    data-testid={`story-comment-del-${c.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-[#F0EBE3] px-4 pt-3 pb-3 flex items-center gap-2 bg-[#FBF7EF]">
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 280))}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder="Aggiungi un commento…"
            maxLength={280}
            className="flex-1 h-11 rounded-full bg-white border border-[#F0EBE3] px-4 text-[14px] outline-none focus:border-[#FF7A1A]"
            data-testid="story-comment-input"
          />
          <button
            onClick={send}
            disabled={!text.trim() || posting}
            data-testid="story-comment-send"
            className="h-11 w-11 rounded-full flex items-center justify-center text-white shrink-0"
            style={{
              background: text.trim() ? "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" : "#E0DAD0",
              boxShadow: text.trim() ? "0 8px 18px -6px rgba(255,122,26,0.55)" : "none",
              opacity: posting ? 0.6 : 1,
            }}
          >
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function timeSince(iso) {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "adesso";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}min fa`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h fa`;
    const d = Math.floor(h / 24);
    return `${d}g fa`;
  } catch { return ""; }
}
